import { useEffect, useMemo, useState, useCallback } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { TAccountTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  GetCreditFacilitiesWithLiabilityAccountsQueryGQLType,
  useGetCreditFacilitiesWithLiabilityAccountsQuery,
} from '~/packages/transactions/requests/getCreditFacilitiesWithLoansAndLiabilityAccounts.gql.generated';

const useLoanAndTAccounts = (
  selectedFacilityIds: string[],
  facilities: GetCreditFacilitiesWithLiabilityAccountsQueryGQLType['creditFacilities'],
) => {
  const selectedFacilityIdMap = useMemo(
    () => Object.fromEntries(selectedFacilityIds.map((id) => [id, true])),
    [selectedFacilityIds],
  );

  const { uniqueTAccounts } = useMemo(() => {
    const filteredFacilities = facilities.filter(
      (facility) => selectedFacilityIdMap[facility.id],
    );
    const tAccounts = [
      ...new Set(
        filteredFacilities.flatMap((facility) =>
          facility.liabilityAccounts.map((tAccount) => tAccount.tAccount),
        ),
      ),
    ];

    return { uniqueTAccounts: tAccounts };
  }, [facilities, selectedFacilityIdMap]);

  return { uniqueTAccounts };
};

// Types
export type TransactionsFilterState = {
  creditFacilityIds: string[];
  loanIds: string[];
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
  accountTypes: TAccountTypeGQLType[];
};

export const useTransactionsFilters = ({
  creditFacilityIds = [],
  loanIds = [],
  accountTypes = [],
  startDate = commonDate.addDays(commonDate.currentDateStr(), -90),
  endDate = commonDate.addDays(commonDate.currentDateStr(), 14),
}: Partial<TransactionsFilterState>) => {
  // Date range
  const [dateRange, setDateRange] = useState({ startDate, endDate });
  // Facilities
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(
    creditFacilityIds,
  );
  // Loans
  const [selectedLoanIds, setSelectedLoanIds] = useState(loanIds);
  // Facilities
  const [selectedAccountTypes, setSelectedAccountTypes] = useState(
    accountTypes,
  );

  const currentFilters = useMemo<TransactionsFilterState>(
    () => ({
      creditFacilityIds: selectedFacilityIds,
      loanIds: selectedLoanIds,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      accountTypes: selectedAccountTypes,
    }),
    [dateRange, selectedAccountTypes, selectedFacilityIds, selectedLoanIds],
  );

  const setFilters = useCallback(
    (nextFilters: Partial<TransactionsFilterState>) => {
      if (nextFilters.startDate && nextFilters.endDate) {
        setDateRange({
          startDate: nextFilters.startDate,
          endDate: nextFilters.endDate,
        });
      }
      if (nextFilters.creditFacilityIds) {
        setSelectedFacilityIds(nextFilters.creditFacilityIds);
      }
      if (nextFilters.loanIds) {
        setSelectedLoanIds(nextFilters.loanIds);
      }
      if (nextFilters.accountTypes) {
        setSelectedAccountTypes(nextFilters.accountTypes);
      }
    },
    [],
  );
  return [currentFilters, setFilters] as const;
};

type Status = 'pending' | 'ready' | 'error';
type ResultCommon = {
  currentFilters: TransactionsFilterState;
  setCurrentFilters: (nextFilters: Partial<TransactionsFilterState>) => void;
};
type Result =
  | (ResultCommon & { status: 'error'; error: Error })
  | (ResultCommon & { status: 'pending' })
  | (ResultCommon & {
      status: 'ready';
      uniqueTAccounts: ReturnType<
        typeof useLoanAndTAccounts
      >['uniqueTAccounts'];
    });
export const useDeriveTransactionsFiltersFromBackend = ({
  creditFacilityIds,
  loanIds,
  startDate,
  endDate,
}: {
  creditFacilityIds: string[];
  loanIds?: string[];
  startDate?: Util.FullDateStr;
  endDate?: Util.FullDateStr;
}): Result => {
  const { user } = useUserAuthOrThrow();
  const [status, setStatus] = useState<Status>('pending');

  const [currentFilters, setCurrentFilters] = useTransactionsFilters({
    creditFacilityIds,
    loanIds,
    startDate,
    endDate,
  });

  // Derive transaction filter state from facility loans and liability accounts
  const facilitiesWithLoansAndLiabilityAccountsQuery = useGetCreditFacilitiesWithLiabilityAccountsQuery(
    { variables: { organizationId: user.organization.id } },
  );
  const { uniqueTAccounts } = useLoanAndTAccounts(
    currentFilters.creditFacilityIds,
    facilitiesWithLoansAndLiabilityAccountsQuery.data?.creditFacilities ?? [],
  );

  useEffect(() => {
    if (facilitiesWithLoansAndLiabilityAccountsQuery.data) {
      setCurrentFilters({ accountTypes: uniqueTAccounts });
      // At this point, the underlying facilities GQL query is already resolved (loading=false), but
      // we have to wait for 1 render cycle for the current filters state to update. We want
      // consumers of this hook to be able to wait for the current filter state to fully propagate.
      // This is done by "queueing up" a status state change to _also_ resolve 1 render cycle later.
      setStatus('ready');
    }
  }, [
    facilitiesWithLoansAndLiabilityAccountsQuery.data,
    setCurrentFilters,
    uniqueTAccounts,
  ]);
  useEffect(() => {
    if (facilitiesWithLoansAndLiabilityAccountsQuery.error) {
      setStatus('error');
    }
  }, [facilitiesWithLoansAndLiabilityAccountsQuery.error]);

  return useMemo(() => {
    if (status === 'error') {
      return {
        status,
        error: facilitiesWithLoansAndLiabilityAccountsQuery.error!,
        currentFilters,
        setCurrentFilters,
      };
    }

    return {
      status,
      uniqueTAccounts,
      currentFilters,
      setCurrentFilters,
    };
  }, [
    status,
    uniqueTAccounts,
    facilitiesWithLoansAndLiabilityAccountsQuery.error,
    currentFilters,
    setCurrentFilters,
  ]);
};
